import type { ReactNode } from "react";

export interface ProductPillProps {
  children: ReactNode;
}

/**
 * La etiqueta flotante sobre la imagen de una tarjeta — la categoría
 * arriba a la izquierda, las unidades arriba a la derecha.
 *
 * ── La forma es la de producción ─────────────────────────────────────
 * Cápsula completa, borde claro de un pelo, cristal esmerilado detrás y
 * el texto en 10 px negrita, versales y muy espaciado (0.2em). De lejos,
 * la firma de la tarjeta es esa: dos pastillas de cristal sobre la foto.
 *
 * ── El fondo NO es el de producción, a propósito ─────────────────────
 * Allá es `bg-white/20`. Con blanco al 20% sobre una fotografía clara el
 * texto blanco cae a ~2:1 y la píldora se borra; comprobado a 1440 px con
 * un producto sin foto, donde el marcador es casi blanco. Aquí el fondo
 * es tinta al 65%, que sobre el peor fondo posible —blanco puro— deja el
 * texto en 5.2:1. Sigue leyéndose como cristal oscuro, y se lee siempre.
 *
 * El velo del gradiente solo cubre la parte de abajo de la tarjeta, así
 * que estas dos píldoras se defienden solas.
 *
 * ── El recorte ───────────────────────────────────────────────────────
 * Va en un hijo de BLOQUE, no en el `inline-flex` de fuera:
 * `text-overflow` actúa sobre la caja de bloque que contiene el texto, y
 * en un contenedor flex ese texto vive en una caja anónima donde los
 * puntos suspensivos no aparecen. El resultado era una presentación larga
 * cortada a hachazos contra el borde de la tarjeta.
 */
export function ProductPill({ children }: ProductPillProps) {
  return (
    <span className="inline-flex min-w-0 max-w-[9rem] items-center rounded-full border border-white/25 bg-ds-ink/65 px-3 py-1.5 text-[10px] font-bold uppercase leading-none tracking-[0.2em] text-white backdrop-blur-2xl">
      <span className="block truncate">{children}</span>
    </span>
  );
}
